<template>
  <div class="grid sm:grid-cols-2 gap-2 sm:gap-4 items-start">
    <!-- Vehicle Details -->
    <Card style="min-height: 82px">
      <CardBody>
        <div class="relative">
          <div class="left-0 top-0 right-0 flex justify-between flex-col lg:flex-row items-center">
            <div class="flex items-start flex-grow">
              <img
                :src="agreementData.vehicle.manufacturerLogoUrl"
                class="mt-1"
                width="30"
              />
              <div class="ml-3">
                <div class="font-semibold text-secondary flex items-center gap-2">
                  {{ agreementData.vehicle.model }}
                  <Icon
                    v-if="isElectricVehicle"
                    v-tippy="'Electric'"
                    type="bolt"
                  />
                  <Icon
                    v-if="isHybridVehicle"
                    v-tippy="'Hybrid'"
                    type="leaf"
                  />
                  <Icon
                    v-if="isPetrolVehicle"
                    v-tippy="'Petrol'"
                    type="gas-pump"
                  />
                  <Icon
                    v-if="isDieselVehicle"
                    v-tippy="'Diesel'"
                    type="gas-pump"
                  />
                  <Chip
                    v-if="agreementData.newUsed"
                    size="sm"
                  >
                    {{ agreementData.newUsed }}
                  </Chip>
                  <Chip
                    v-if="agreementData.vehicle.modelYear"
                    size="sm"
                  >
                    {{ agreementData.vehicle.modelYear }}
                  </Chip>
                </div>
                <div class="text-sm text-quaternary pr-4">
                  {{ agreementData.vehicle.derivative }}
                </div>
              </div>
            </div>
            <div class="flex items-center mt-5 lg:mt-0">
              <CopyText :explicit-text-to-copy="agreementData.vehicle.vrm">
                <LicensePlate
                  :plate="agreementData.vehicle.vrm"
                  country="uk"
                  :design="isElectricVehicle ? 'ev' : ''"
                  greyscale
                />
              </CopyText>
            </div>
          </div>

          <div class="flex justify-center -mt-[10px] -mb-[60px] mx-8">
            <img
              v-show="isVehicleImageLoaded"
              :src="agreementData.vehicle.imageUrl ?? undefined"
              @load="isVehicleImageLoaded = true"
            />
            <Skeleton
              v-if="!isVehicleImageLoaded"
              width="100%"
              height="15rem"
              class="mb-2"
            />
          </div>
        </div>
      </CardBody>
      <div
        class="bg-gradient-to-t from-transparent via-transparent to-bg-tertiary rounded-xl rounded-t-[2rem] p-6 -mt-[70px] pt-[70px]"
      >
        <MileageTracker
          :mileage-data="{
            unit: agreementData.odometer.unit === 'miles' ? 'mi' : 'km',
            atStart: agreementData.odometer.atStart ?? 0,
            current: agreementData.odometer.current ?? 0,
            expected: agreementData.odometer.expected ?? 0,
            atEnd: agreementData.odometer.allowanceTotal ?? 0
          }"
        />
        <div class="flex justify-center">
          <Button
            label="Update Mileage"
            icon="dashboard"
            plain
            @click="$emit('open-update-mileage-modal')"
          />
        </div>
      </div>
    </Card>

    <!-- Finance Summary -->
    <div>
      <Card>
        <CardBody>
          <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div class="flex items-center gap-4 min-w-0">
              <div class="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-tertiary">
                <Icon
                  :type="agreementOpportunityIcon"
                  class="text-4xl"
                  :class="agreementOpportunityColorClass"
                />
              </div>

              <div class="min-w-0">
                <div class="flex items-center gap-2 text-sm text-quaternary">
                  <span>Opportunity</span>
                  <Icon
                    v-tippy="{ content: 'Based on the last imported data for this customer.' }"
                    type="circle-info"
                    class="cursor-help text-secondary"
                  />
                </div>
                <div
                  class="text-2xl font-bold"
                  :class="agreementOpportunityColorClass"
                >
                  {{ agreementOpportunityLabel }}
                </div>
              </div>
            </div>

            <div class="flex flex-col items-end gap-4">
              <div class="grid grid-cols-2 gap-6 md:gap-8">
                <div>
                  <div class="text-sm text-quaternary">Equity</div>
                  <div class="text-xl font-bold text-secondary">
                    {{ formatNumber(displayedOpportunityEquity, 'currency', 0, agreementData.currency) }}
                  </div>
                </div>
                <div>
                  <div class="text-sm text-quaternary">Settlement</div>
                  <div class="text-xl font-bold text-secondary">
                    {{ formatNumber(importedSettlementFigure, 'currency', 0, agreementData.currency) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="hasAgreementVehicleOffers"
            class="mt-6 border-t border-secondary pt-6"
          >
            <div class="mb-3 flex items-center justify-between gap-4">
              <div class="text-sm text-quaternary">Offers</div>
              <div class="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-secondary">
                <ToggleSwitch
                  v-model="includeOffers"
                  size="sm"
                />
                <div class="font-medium">Include Offers</div>
              </div>
            </div>

            <RadioBoxed
              v-model="selectedOfferId"
              :options="agreementVehicleOfferOptions"
              layout="column"
              size="sm"
              :equal-widths="false"
            >
              <template #option="{ option }">
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0">
                    <div class="font-semibold text-secondary">
                      {{ getAgreementVehicleOfferOption(option).title }}
                    </div>
                    <div class="text-sm text-quaternary">
                      {{ getAgreementVehicleOfferOption(option).dateRange }}
                    </div>
                  </div>
                  <div class="text-right whitespace-nowrap">
                    <div class="text-sm text-quaternary">Deposit Contribution</div>
                    <div class="font-semibold text-secondary">
                      {{ getAgreementVehicleOfferOption(option).depositContribution }}
                    </div>
                  </div>
                </div>
              </template>
            </RadioBoxed>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <div>
            <TermTracker
              :start-date="agreementData.startDate"
              :end-date="agreementData.endDate"
            />
          </div>
        </CardBody>

        <!-- Details Grid -->
        <div class="border-y border-secondary grid grid-cols-1 lg:grid-cols-3 leading-6 gap-[1px] bg-quaternary">
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Product</div>
            <div class="text-xl font-bold text-secondary">
              {{ $t('ui.entities.financeProducts.' + agreementData.productType) }}
            </div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Monthly Payment</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.monthlyRepayment, 'currency', 2, agreementData.currency) }}
            </div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Final Payment</div>
            <div class="text-xl font-bold text-secondary">
              {{
                agreementData.finalRepayment
                  ? formatNumber(agreementData.finalRepayment, 'currency', 2, agreementData.currency)
                  : '-'
              }}
            </div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">APR</div>
            <div class="text-xl font-bold text-secondary">{{ agreementData.rate }}%</div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Term</div>
            <div class="text-xl font-bold text-secondary">{{ agreementData.term }}m</div>
          </div>
          <div class="py-4 px-6 bg-primary-alt">
            <div class="text-sm text-quaternary">Deposit</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.deposit, 'currency', 2, agreementData.currency) }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Purchase Price</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.purchasePrice, 'currency', 0, agreementData.currency) }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Amount Financed</div>
            <div class="text-xl font-bold text-secondary">
              {{ formatNumber(agreementData.amountBorrowed, 'currency', 0, agreementData.currency) }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Distance Allowed</div>
            <div class="text-xl font-bold text-secondary">
              {{
                agreementData.odometer.allowanceYearly
                  ? formatAbbreviatedNumber(agreementData.odometer.allowanceYearly, 2, false) +
                    ' ' +
                    agreementData.odometer.unit
                  : '-'
              }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt"
          >
            <div class="text-sm text-quaternary">Reg Date</div>
            <div class="text-xl font-bold text-secondary">
              {{ agreementData.vehicle.regDate ? dayjs(agreementData.vehicle.regDate).format('DD/MM/YYYY') : '' }}
            </div>
          </div>
          <div
            v-if="showFullDetails"
            class="py-4 px-6 bg-primary-alt col-span-2"
          >
            <div class="text-sm text-quaternary">VIN</div>
            <div class="text-xl font-bold text-secondary">
              {{ agreementData.vehicle.vin }}
            </div>
          </div>
        </div>

        <!-- More details Button -->
        <div class="py-4 items-center justify-center flex">
          <Button
            severity="default"
            :label="showFullDetails ? 'Hide Details' : 'More Details'"
            text
            :icon="showFullDetails ? 'chevron-up' : 'chevron-down'"
            icon-pos="right"
            @click="$emit('toggle-full-details')"
          />
        </div>
      </Card>
    </div>

    <!-- Latest Activity -->
    <Card>
      <CardBody>
        <AgreementTimeline
          :items="localRecentTimelineData"
          :agreement-i-d="agreementID"
          @update:items="handleTimelineUpdate"
          @note-added="$emit('note-added', $event)"
          @edit-note="$emit('edit-note', $event)"
        />
        <div class="mt-2 flex justify-center">
          <Button
            plain
            severity="secondary"
            icon-pos="right"
            label="View all activity"
            :router-link="`/agreement/${agreementID}/activity`"
          />
        </div>
      </CardBody>
    </Card>

    <div class="grid grid-cols-1 gap-2">
      <!-- Appointments -->
      <Card>
        <CardBody>
          <AgreementAppointments
            :appointments-list="appointmentsData ?? undefined"
            compact
            :future-only="false"
          />
          <div class="mt-2 flex justify-center">
            <Button
              v-if="appointmentsData && appointmentsData.length > 0"
              plain
              severity="secondary"
              icon-pos="right"
              label="View all appointments"
              :router-link="`/agreement/${agreementID}/appointments`"
            />
          </div>
        </CardBody>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
// composables
import { ref, computed, watch } from 'vue'
// components
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import Icon from '@/components/icon/Icon.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import RadioBoxed from '@/components/forms/RadioBoxed.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import CopyText from '@/components/CopyText.vue'
import LicensePlate from '@/components/license-plate/LicensePlate.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import MileageTracker from '@/components/unique/trackers/MileageTracker.vue'
import TermTracker from '@/components/unique/trackers/TermTracker.vue'
import AgreementTimeline from '@/components/unique/agreement/AgreementTimeline.vue'
import AgreementAppointments from '@/components/unique/agreement/AgreementAppointments.vue'
// utilities
import { getDictionaryItem } from '@/dictionary'
import dayjs from 'dayjs'
import { formatAbbreviatedNumber, formatNumber } from '@/utilities/number'
// types
import type { AgreementOpportunityItem } from '@/dictionary'
import type { Agreement, AgreementOpportunitySnapshot } from '@/types/domain/agreement'
import type { VehicleOffer } from '@/types/domain/vehicleOffer'

interface Props {
  agreementData: Agreement
  agreementID: string | number
  recentTimelineData: Array<Record<string, unknown>>
  appointmentsData: Array<Record<string, unknown>> | null
  showFullDetails: boolean
}

interface AgreementVehicleOfferOption {
  value: number
  label: string
  title: string
  dateRange: string
  depositContribution: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'open-update-mileage-modal': []
  'toggle-full-details': []
  'note-added': [item: Record<string, unknown>]
  'edit-note': [item: Record<string, unknown>]
  'update:recentTimelineData': [items: Array<Record<string, unknown>>]
}>()

const isVehicleImageLoaded = ref(false)

/**
 * Lowercased fuel type, including decoded values such as Electric or Petrol/MHEV.
 */
const vehicleFuelType = computed(() => (props.agreementData.vehicle.fuelType ?? '').toLowerCase())

/**
 * Whether the vehicle should use the electric icon and plate.
 */
const isElectricVehicle = computed(() => vehicleFuelType.value === 'electric' || vehicleFuelType.value === 'ev')

/**
 * Whether a hybrid marker should take precedence over petrol or diesel.
 */
const isHybridVehicle = computed(() => {
  if (isElectricVehicle.value) {
    return false
  }

  return (
    vehicleFuelType.value.includes('hybrid') ||
    vehicleFuelType.value.includes('phev') ||
    vehicleFuelType.value.includes('mhev')
  )
})

/**
 * Whether the fuel is petrol and not a hybrid or electric variant.
 */
const isPetrolVehicle = computed(() => {
  if (isElectricVehicle.value || isHybridVehicle.value) {
    return false
  }

  return vehicleFuelType.value.includes('petrol')
})

/**
 * Whether the fuel is diesel and not a hybrid or electric variant.
 */
const isDieselVehicle = computed(() => {
  if (isElectricVehicle.value || isHybridVehicle.value) {
    return false
  }

  return vehicleFuelType.value.includes('diesel')
})
const includeOffers = ref(true)
const selectedOfferId = ref<number | null>(null)

/**
 * Whether the agreement has offer rows available to show.
 */
const hasAgreementVehicleOffers = computed(() => agreementVehicleOffers.value.length > 0)

/**
 * Relevant vehicle offers returned with the agreement payload.
 */
const agreementVehicleOffers = computed<VehicleOffer[]>(() => {
  return Array.isArray(props.agreementData.vehicleOffers) ? props.agreementData.vehicleOffers : []
})

/**
 * Best offer identifier returned by the agreement opportunity payload.
 */
const bestOfferId = computed<number | null>(() => {
  return props.agreementData.opportunity?.withBestOffer?.offerId ?? null
})

/**
 * Default selected offer for the boxed radio list.
 */
const defaultSelectedOfferId = computed<number | null>(() => {
  if (bestOfferId.value !== null && agreementVehicleOffers.value.some(offer => offer.id === bestOfferId.value)) {
    return bestOfferId.value
  }

  return agreementVehicleOffers.value[0]?.id ?? null
})

/**
 * Offer currently selected in the boxed radio list.
 */
const selectedAgreementVehicleOffer = computed<VehicleOffer | null>(() => {
  if (selectedOfferId.value === null) {
    return null
  }

  return agreementVehicleOffers.value.find(offer => offer.id === selectedOfferId.value) ?? null
})

/**
 * RadioBoxed options for agreement offers.
 */
const agreementVehicleOfferOptions = computed(() => {
  return agreementVehicleOffers.value.map(offer => ({
    value: offer.id,
    label: offer.title,
    title: offer.title,
    dateRange: formatVehicleOfferDateRange(offer),
    depositContribution: formatOfferDepositContribution(offer)
  }))
})

/**
 * Selected opportunity payload based on whether offers are included.
 */
const selectedOpportunitySnapshot = computed<AgreementOpportunitySnapshot | null>(() => {
  const opportunity = props.agreementData.opportunity

  if (!opportunity) {
    return null
  }

  if (includeOffers.value && hasAgreementVehicleOffers.value) {
    if (selectedAgreementVehicleOffer.value && selectedAgreementVehicleOffer.value.id === bestOfferId.value) {
      return opportunity.withBestOffer ?? opportunity.default ?? null
    }

    if (selectedAgreementVehicleOffer.value) {
      return {
        equity: (props.agreementData.equity ?? 0) + selectedAgreementVehicleOffer.value.depositContribution,
        label: opportunity.default?.label ?? opportunity.withBestOffer?.label ?? null,
        offerId: selectedAgreementVehicleOffer.value.id
      }
    }

    return opportunity.withBestOffer ?? opportunity.default ?? null
  }

  return opportunity.default ?? opportunity.withBestOffer ?? null
})

/**
 * Dictionary metadata for the imported opportunity rating on the agreement.
 */
const agreementOpportunity = computed<AgreementOpportunityItem | null>(() => {
  const opportunityLabel = selectedOpportunitySnapshot.value?.label

  if (!opportunityLabel) {
    return null
  }

  return (getDictionaryItem('agreementOpportunity', opportunityLabel) as AgreementOpportunityItem | null) ?? null
})

/**
 * Label displayed for the compact agreement opportunity summary.
 */
const agreementOpportunityLabel = computed(() => agreementOpportunity.value?.label ?? 'No opportunity')

/**
 * Icon displayed for the compact agreement opportunity summary.
 */
const agreementOpportunityIcon = computed(() => agreementOpportunity.value?.icon ?? 'fire')

/**
 * Text color used for the imported opportunity summary content.
 */
const agreementOpportunityColorClass = computed(() => {
  return agreementOpportunity.value ? `text-${agreementOpportunity.value.color}` : 'text-secondary'
})

/**
 * Equity value displayed for the selected opportunity state.
 */
const displayedOpportunityEquity = computed(() => {
  return selectedOpportunitySnapshot.value?.equity ?? props.agreementData.equity
})

/**
 * Settlement figure shown from the latest imported dataset, with a fallback for older records.
 */
const importedSettlementFigure = computed(
  () => props.agreementData.settlementOnImport || props.agreementData.settlement
)

// Local computed for timeline data to allow v-model
const localRecentTimelineData = computed({
  get: () => props.recentTimelineData,
  set: value => {
    emit('update:recentTimelineData', value)
  }
})

watch(
  [agreementVehicleOffers, defaultSelectedOfferId],
  () => {
    if (!agreementVehicleOffers.value.some(offer => offer.id === selectedOfferId.value)) {
      selectedOfferId.value = defaultSelectedOfferId.value
    }
  },
  { immediate: true }
)

function handleTimelineUpdate(items: unknown[]) {
  emit('update:recentTimelineData', items as Array<Record<string, unknown>>)
}

/**
 * Formats the offer date range for compact agreement display.
 */
function formatVehicleOfferDateRange(offer: VehicleOffer): string {
  const startDate = dayjs(offer.startDate)
  const endDate = dayjs(offer.endDate)

  if (!startDate.isValid() || !endDate.isValid()) {
    return 'Date unavailable'
  }

  return `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`
}
/**
 * Formats the offer deposit contribution for compact agreement display.
 */
function formatOfferDepositContribution(offer: VehicleOffer): string {
  return formatNumber(offer.depositContribution, 'currency', 0, props.agreementData.currency) ?? '-'
}

/**
 * Narrows a boxed radio slot option into an agreement offer option payload.
 */
function getAgreementVehicleOfferOption(option: unknown): AgreementVehicleOfferOption {
  if (option && typeof option === 'object') {
    const candidate = option as Partial<AgreementVehicleOfferOption>

    return {
      value: typeof candidate.value === 'number' ? candidate.value : 0,
      label: typeof candidate.label === 'string' ? candidate.label : '',
      title: typeof candidate.title === 'string' ? candidate.title : '',
      dateRange: typeof candidate.dateRange === 'string' ? candidate.dateRange : 'Date unavailable',
      depositContribution: typeof candidate.depositContribution === 'string' ? candidate.depositContribution : '-'
    }
  }

  return {
    value: 0,
    label: '',
    title: '',
    dateRange: 'Date unavailable',
    depositContribution: '-'
  }
}
</script>
